/**
 * Display helpers for real system data (timestamps, dates). Anything formatted
 * here is set in Plex Mono per the brand system — it is data the product
 * produced, not decoration.
 */

/** `2026-07-30 14:05` in the viewer's locale, or the raw value if unparsable. */
export function formatTimestamp(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Coarse "how long ago", for a recovered draft. Deliberately imprecise — the
 * point is to place the work in time, not to measure it. Falls back to the raw
 * value when the timestamp is unparsable.
 */
export function formatRelativeTime(value: string, now: Date = new Date()): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  const elapsed = now.getTime() - parsed.getTime();
  // Negative elapsed means a skewed clock, not the future.
  if (elapsed < MINUTE_MS) return 'a moment ago';

  if (elapsed < HOUR_MS) {
    const minutes = Math.floor(elapsed / MINUTE_MS);
    return minutes === 1 ? 'a minute ago' : `${minutes} minutes ago`;
  }

  if (elapsed < DAY_MS) {
    const hours = Math.floor(elapsed / HOUR_MS);
    return hours === 1 ? 'an hour ago' : `${hours} hours ago`;
  }

  const days = Math.floor(elapsed / DAY_MS);
  return days === 1 ? 'a day ago' : `${days} days ago`;
}

/** Anything older than this reads as a date; "94d ago" is not information. */
const RELATIVE_AGE_LIMIT_DAYS = 30;

/**
 * Compact age for a feed item: `just now`, `4m ago`, `3h ago`, `2d ago`.
 *
 * The long form above is for one recovered draft; a list of headlines wants the
 * short one, and wants it to stop being relative once it stops being recent.
 * No date library — the arithmetic is three constants and a floor.
 */
export function formatRelativeAge(value: string, now: Date = new Date()): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  // Negative elapsed means a skewed clock or a hopeful publisher, not the future.
  const elapsed = now.getTime() - parsed.getTime();
  if (elapsed < MINUTE_MS) return 'just now';
  if (elapsed < HOUR_MS) return `${Math.floor(elapsed / MINUTE_MS)}m ago`;
  if (elapsed < DAY_MS) return `${Math.floor(elapsed / HOUR_MS)}h ago`;

  const days = Math.floor(elapsed / DAY_MS);
  return days > RELATIVE_AGE_LIMIT_DAYS ? formatInstantDate(value) : `${days}d ago`;
}

/**
 * The calendar date of an instant, in the viewer's timezone. For data that is a
 * timestamp but reads as a day — when a post went out, not at what minute.
 */
export function formatInstantDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

/** Calendar date without a time component. Falls back to the raw value. */
export function formatDate(value: string): string {
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

/**
 * The marker for one of a request's two drafts: `variant_index` 1 reads "A",
 * 2 reads "B". The pair is "two versions"; one of them is "Version A".
 */
export function versionLetter(variantIndex: number): string {
  return variantIndex >= 1 && variantIndex <= 26
    ? String.fromCharCode(64 + variantIndex)
    : String(variantIndex);
}
